import React, { useCallback, useMemo, useRef, useState } from 'react';

import { link } from '@aline/routes';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Card, Skeleton, Typography } from '@mui/material';
import clsx from 'clsx';
import { differenceInWeeks, parseISO } from 'date-fns';
import { PlusCircle } from 'lucide-react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Apollo } from 'src/gql/generated';
import { DateKey, Direction, getDateComparator } from 'src/helpers';
import { useGetBoardColumnWorkflows } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import type { BoardLocalState } from './Board';
import { CreateWorkflowDialog } from './CreateWorkflowDialog';
import { getColumnDndId, getWorkflowDndId } from './utils/board-dnd-ids';
import { SortableWorkflowCard } from './workflow-card/SortableWorkflowCard';

export const columnWidthPx = 383;

const useStyles = makeStyles()(theme => ({
  column: {
    border: '2px solid transparent',
    borderRadius: 3,
    boxSizing: 'border-box',
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    maxWidth: columnWidthPx,
    minHeight: 0,
    minWidth: columnWidthPx,
    overflow: 'auto',
    padding: theme.spacing(0, 1),
    transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
  },
  columnDragOver: {
    backgroundColor: 'rgba(60, 72, 90, 0.05)',
    borderColor: theme.palette.primary.main,
  },
  emptyCard: {
    '&:hover': {
      borderColor: theme.palette.primary.main,
      cursor: 'pointer',
    },
    alignItems: 'center',
    backgroundColor: theme.appColors.background,
    border: `2px dashed ${theme.appColors.rule}`,
    borderRadius: 8,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    justifyContent: 'center',
    minHeight: 100,
    padding: theme.spacing(3),
    transition: 'border-color 0.2s ease-in-out',
    width: '100%',
  },
  emptyCardIcon: {
    color: theme.palette.primary.main,
  },
  emptyCardText: {
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  emptyColumn: {
    alignItems: 'flex-start',
    paddingTop: theme.spacing(1),
  },
  filteredEmptyCard: {
    alignItems: 'center',
    backgroundColor: theme.appColors.background,
    border: `2px dashed ${theme.appColors.rule}`,
    borderRadius: 8,
    boxSizing: 'border-box',
    color: theme.palette.text.secondary,
    display: 'flex',
    justifyContent: 'center',
    minHeight: 100,
    padding: theme.spacing(3),
    textAlign: 'center',
    width: '100%',
  },
  infiniteScrollContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  loadingCard: {
    '&:last-child': { marginBottom: theme.spacing(1) },
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    overflow: 'unset',
    padding: theme.spacing(2),
  },
  loadingCardRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
  },
}));

export const useColumnStyles = useStyles;

function LoadingSkeletonCard(): JSX.Element {
  const { classes } = useStyles();
  return (
    <Card className={classes.loadingCard}>
      <div className={classes.loadingCardRow}>
        <Skeleton height={20} variant="text" width="70%" />
      </div>
      <div className={classes.loadingCardRow}>
        <Skeleton height={24} variant="rectangular" width={60} />
      </div>
      <div className={classes.loadingCardRow}>
        <Skeleton height={20} variant="text" width={50} />
        <Skeleton height={20} variant="text" width={30} />
      </div>
      <div className={classes.loadingCardRow}>
        <Skeleton height={24} variant="rectangular" width={70} />
        <Skeleton height={32} variant="circular" width={32} />
      </div>
    </Card>
  );
}

interface EmptyColumnCardProps {
  boardId?: string;
  columnId?: string;
}

function EmptyColumnCard({ boardId, columnId }: EmptyColumnCardProps): JSX.Element {
  const { classes } = useStyles();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <>
      <div
        className={classes.emptyCard}
        role="button"
        tabIndex={0}
        onClick={() => setIsDialogOpen(true)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') setIsDialogOpen(true);
        }}
      >
        <PlusCircle className={classes.emptyCardIcon} size={24} />
        <Typography className={classes.emptyCardText} variant="body2">
          Add a workflow
        </Typography>
      </div>
      <CreateWorkflowDialog
        initialBoardId={boardId}
        initialColumnId={columnId}
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      />
    </>
  );
}

type BoardWorkflow = NonNullable<Apollo.GetBoardWorkflowsQuery['workflows']['rows']>[number];

interface WorkflowsListProps {
  loading: boolean;
  workflows: BoardWorkflow[];
  isDragDisabled?: boolean;
}

function WorkflowsList({ isDragDisabled, loading, workflows }: WorkflowsListProps): JSX.Element {
  if (loading && !workflows.length) {
    return (
      <>
        <LoadingSkeletonCard />
        <LoadingSkeletonCard />
      </>
    );
  }

  return (
    <>
      {workflows.map(workflow => (
        <SortableWorkflowCard
          key={workflow.id}
          disabled={isDragDisabled}
          linkTo={
            workflow.variant === Apollo.WorkflowVariant.Template
              ? link.TemplateSelectedTemplateIndex({ workflowId: String(workflow.id) })
              : link.WorkflowDetail({ workflowId: String(workflow.id) })
          }
          workflow={workflow}
        />
      ))}
    </>
  );
}

interface BoardColumnProps {
  boardState: BoardLocalState;
  category: Apollo.ColumnCategory;
  columnId: UUID;
  boardId?: UUID;
  isDragDisabled?: boolean;
}

export function BoardColumn({
  boardId,
  boardState,
  category,
  columnId,
  isDragDisabled,
}: BoardColumnProps): JSX.Element {
  const { classes } = useStyles();
  const columnRef = useRef<HTMLDivElement>(null);
  const scrollableId = `board-column-${columnId}`;

  // Droppable zone setup
  const { isOver, setNodeRef } = useDroppable({
    id: getColumnDndId(columnId),
  });

  // Fetch workflows for this column
  const { fetchMoreWorkflows, hasMore, loading, workflows } = useGetBoardColumnWorkflows({
    apolloOpts: {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
    boardId,
    boardState,
    category,
    columnId,
  });

  /**
   * Sort workflows by updatedAt (most recent first). Done columns only keep the
   * last 4 weeks so they don't grow without bound. A workflow parked in Done can
   * still have a null completedAt — it landed there before the server stamped
   * completion on the move, or later phase progress reopened it — so fall back to
   * updatedAt instead of dropping the card off the board entirely.
   */
  const sortedWorkflows = useMemo(() => {
    const filtered =
      category === Apollo.ColumnCategory.Done
        ? workflows.filter(w => isWithinWeeks(w.completedAt ?? w.updatedAt, 4))
        : workflows;
    return [...filtered].sort(
      getDateComparator({ dateKey: DateKey.updatedAt, direction: Direction.Descending })
    );
  }, [category, workflows]);

  const workflowIds = useMemo(
    () => sortedWorkflows.map(w => getWorkflowDndId(w.id)),
    [sortedWorkflows]
  );
  const hasFilteredOutWorkflows =
    category === Apollo.ColumnCategory.Done && workflows.length > 0 && sortedWorkflows.length === 0;

  const handleFetchMore = useCallback(async () => {
    if (!loading) await fetchMoreWorkflows();
  }, [loading, fetchMoreWorkflows]);

  // Combine refs for droppable and scrollable
  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      columnRef.current = node;
    },
    [setNodeRef]
  );

  // Empty column state
  if (!loading && !sortedWorkflows.length) {
    return (
      <div
        ref={setRefs}
        className={clsx(classes.column, classes.emptyColumn, isOver && classes.columnDragOver)}
        id={scrollableId}
      >
        {hasFilteredOutWorkflows ? (
          <div className={classes.filteredEmptyCard}>
            <Typography variant="body2">No workflows completed in the last 4 weeks.</Typography>
          </div>
        ) : (
          <EmptyColumnCard boardId={boardId} columnId={columnId} />
        )}
      </div>
    );
  }

  return (
    <div
      ref={setRefs}
      className={clsx(classes.column, isOver && classes.columnDragOver)}
      id={scrollableId}
    >
      <SortableContext items={workflowIds} strategy={verticalListSortingStrategy}>
        <InfiniteScroll
          className={classes.infiniteScrollContainer}
          dataLength={sortedWorkflows.length}
          hasMore={hasMore}
          loader={
            <>
              <LoadingSkeletonCard />
              <LoadingSkeletonCard />
            </>
          }
          next={handleFetchMore}
          scrollableTarget={scrollableId}
        >
          <WorkflowsList
            isDragDisabled={isDragDisabled}
            loading={loading}
            workflows={sortedWorkflows}
          />
        </InfiniteScroll>
      </SortableContext>
    </div>
  );
}

function isWithinWeeks(dateString: string | null, maxWeeksAgo: number): boolean {
  if (!dateString) return false;
  const date = parseISO(dateString);
  return differenceInWeeks(new Date(), date) <= maxWeeksAgo;
}

/** @deprecated Use isWithinWeeks instead */
export function getIsNWeeksAgoOrLess(dateString: string | null, maxWeeksAgo: number): boolean {
  return isWithinWeeks(dateString, maxWeeksAgo);
}
